import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { LOCAL_STORAGE_TOKEN_KEY } from 'commons';
import LOCALS from 'commons/locals';
import i18n from 'i18n';

export type CachedAccount = {
  adminId: number;
  username: string;
  nickName: string;
  /** 含 Bearer 前缀，与 LOCAL_STORAGE_TOKEN_KEY 一致 */
  token: string;
  /** JWT exp，毫秒时间戳；解析失败则为 null */
  expiresAt: number | null;
  lastUsedAt: number;
};

export const CACHED_ACCOUNTS_KEY = 'CACHED_ACCOUNTS';

const normalizeToken = (token: string) => {
  const raw = token.trim();
  if (!raw) return '';
  return raw.startsWith('Bearer ') ? raw : `Bearer ${raw}`;
};

export const parseJwtExpiresAt = (token: string): number | null => {
  try {
    const raw = normalizeToken(token).replace(/^Bearer\s+/i, '');
    if (!raw) return null;
    const { exp } = jwtDecode<{ exp?: number }>(raw);
    if (!exp) return null;
    return exp * 1000;
  } catch {
    return null;
  }
};

export const isTokenExpired = (expiresAt: number | null) => {
  if (expiresAt == null) return true;
  return Date.now() >= expiresAt;
};

export const formatTokenExpiry = (expiresAt: number | null) => {
  if (expiresAt == null) {
    return String(i18n.t(LOCALS.token_expiry_unknown));
  }
  const time = dayjs(expiresAt).format('YYYY-MM-DD HH:mm');
  if (isTokenExpired(expiresAt)) {
    return String(i18n.t(LOCALS.token_expired_at, { time }));
  }
  return String(i18n.t(LOCALS.token_valid_until, { time }));
};

export const getCachedAccounts = (): CachedAccount[] => {
  try {
    const raw = window.localStorage.getItem(CACHED_ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is CachedAccount =>
          !!item &&
          typeof item.adminId === 'number' &&
          typeof item.username === 'string' &&
          typeof item.token === 'string',
      )
      .map((item) => ({
        ...item,
        token: normalizeToken(item.token),
        nickName: item.nickName || item.username,
        expiresAt: item.expiresAt ?? parseJwtExpiresAt(item.token) ?? null,
        lastUsedAt: item.lastUsedAt || 0,
      }))
      .sort((a, b) => (b.lastUsedAt || 0) - (a.lastUsedAt || 0));
  } catch {
    return [];
  }
};

export const setCachedAccounts = (accounts: CachedAccount[]) => {
  window.localStorage.setItem(CACHED_ACCOUNTS_KEY, JSON.stringify(accounts));
};

/** 登录成功 / 拉取用户信息后写入或更新本机账号 token 缓存 */
export const upsertCachedAccount = (input: {
  adminId: number;
  username: string;
  nickName?: string;
  token: string;
}) => {
  if (input.adminId == null || !input.token) return;

  const token = normalizeToken(input.token);
  const list = getCachedAccounts().filter(
    (item) => item.adminId !== input.adminId,
  );
  list.unshift({
    adminId: input.adminId,
    username: input.username,
    nickName: input.nickName || input.username,
    token,
    expiresAt: parseJwtExpiresAt(token),
    lastUsedAt: Date.now(),
  });
  setCachedAccounts(list);
};

export const removeCachedAccount = (adminId: number) => {
  setCachedAccounts(
    getCachedAccounts().filter((item) => item.adminId !== adminId),
  );
};

export const getCurrentCachedAccount = (): CachedAccount | null => {
  const currentToken = normalizeToken(
    window.localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY) || '',
  );
  if (!currentToken) return null;
  return (
    getCachedAccounts().find((item) => item.token === currentToken) || null
  );
};

/** 切换到缓存账号：写入当前 token 并刷新 */
export const switchToCachedAccount = (adminId: number) => {
  const account = getCachedAccounts().find((item) => item.adminId === adminId);
  if (!account) {
    throw new Error(String(i18n.t(LOCALS.account_not_found)));
  }
  if (isTokenExpired(account.expiresAt)) {
    throw new Error(String(i18n.t(LOCALS.token_expired_relogin)));
  }
  window.localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, account.token);
  upsertCachedAccount(account);
  if (window.location.pathname === '/login') {
    window.location.href = '/';
  } else {
    window.location.reload();
  }
};

/** 退出当前会话：清除当前 token，并从缓存移除当前账号 */
export const logoutCurrentAccount = () => {
  const current = getCurrentCachedAccount();
  window.localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
  if (current) {
    removeCachedAccount(current.adminId);
  }
};
